import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule, Type } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
    AccountService,
    ActivityStreamService,
    AnimalFeedingService,
    AppointmentService,
    AppReviewService,
    AppStoreService,
    AssignmentPlanService,
    BrowserService,
    CalendarService,
    CameraService,
    CommonService,
    ContactFormService,
    DeepLinkService,
    DeviceService,
    DocumentReferenceService,
    DocumentsFromVetService,
    DocumentViewerService,
    FavoriteReferenceDataService,
    FinancesService,
    FormatService,
    GeolocationService,
    HealthRecordService,
    KeyboardService,
    KnowledgeDbService,
    LoadingService,
    LoggingService,
    LoginService,
    MayBaseService,
    MediaViewerService,
    MedicationPlanService,
    MedicationService,
    NavigationStackService,
    NotificationService,
    OnlinePaymentService,
    PatientService,
    PdfViewerService,
    PermissionService,
    PlatformService,
    PushNotificationService,
    PxlApplicationStateService,
    RouteReuseService,
    SafeAreaService,
    ScreenOrientationService,
    SelfCheckInService,
    ShareService,
    SplashScreenService,
    StatisticService,
    StatusBarService,
    StorageService,
    SystemBroadcastService,
    TermsOfUseService,
    ThemeService,
    UnregisteredDataService,
    UserService,
    VeterinarianLinkageRequestService,
    VeterinarianSearchService,
    VeterinarianService,
} from '@pxl/models/contracts';
import { ApiModule } from '@pxl/shared/generated';

import {
    DefaultAccountService,
    DefaultActivityStreamService,
    DefaultAnimalFeedingService,
    DefaultAppReviewService,
    DefaultAssignmentPlanService,
    DefaultCommonService,
    DefaultContactFormService,
    DefaultDocumentReferenceService,
    DefaultDocumentsFromVetService,
    DefaultFavoriteReferenceDataService,
    DefaultFormatService,
    DefaultKnowledgeDbService,
    DefaultLoginService,
    DefaultMayBaseService,
    DefaultPatientService,
    DefaultRouteReuseService,
    DefaultSelfCheckInService,
    DefaultTermsOfUseService,
    DefaultThemeService,
    DefaultUnregisteredDataService,
    DefaultUserService,
    DefaultVeterinarianLinkageRequestService,
    DefaultVeterinarianService,
    FeatureService,
} from './services';
import { DefaultAppointmentService } from './services/appointment.service';
import { DefaultOnlinePaymentService } from './services/default-online-payment.service';
import { DefaultDocumentViewerService } from './services/document-viewer.service';
import { DefaultLoggingService } from './services/logging.service';
import { DefaultMedicationService } from './services/medication.service';
import { DefaultMedicationPlanService } from './services/plans/medication-plan.service';
import { DefaultVeterinarianSearchService } from './services/veterinarian-search.service';
import { DefaultSystemBroadcastService } from './services/system-messages.service';
import { TranslatedTitleStrategy } from './services/translated-title-strategy.service.service';
import { TitleStrategy } from '@angular/router';
import { DefaultStatisticService } from './services/statistic.service';

export abstract class SharedServicesConfig {
    public abstract accountService?: Type<AccountService>;
    public abstract activityStreamService?: Type<ActivityStreamService>;
    public abstract applicationStateService: Type<PxlApplicationStateService>;
    public abstract commonService?: Type<CommonService>;
    public abstract contactFormService?: Type<ContactFormService>;
    public abstract favoriteReferenceDataService?: Type<FavoriteReferenceDataService>;
    public abstract financesService: Type<FinancesService>;
    public abstract healthRecordService: Type<HealthRecordService>;
    public abstract formatService?: Type<FormatService>;
    public abstract loadingService: Type<LoadingService>;
    public abstract loginService?: Type<LoginService>;
    public abstract routeReuseService?: Type<DefaultRouteReuseService>;
    public abstract termsOfUseService?: Type<TermsOfUseService>;
    public abstract themeService?: Type<DefaultThemeService>;
    public abstract userService?: Type<UserService>;
    public abstract storageService: Type<StorageService>;
    public abstract platformService: Type<PlatformService>;
    public abstract patientService?: Type<PatientService>;
    public abstract shareService: Type<ShareService>;
    public abstract statusBarService: Type<StatusBarService>;
    public abstract splashScreenService: Type<SplashScreenService>;
    public abstract cameraService: Type<CameraService>;
    public abstract permissionService: Type<PermissionService>;
    public abstract geolocationService: Type<GeolocationService>;
    public abstract deepLinkService: Type<DeepLinkService>;
    public abstract keyboardService: Type<KeyboardService>;
    public abstract deviceService: Type<DeviceService>;
    public abstract safeAreaService: Type<SafeAreaService>;
    public abstract BrowserService: Type<BrowserService>;
    public abstract screenOrientationService: Type<ScreenOrientationService>;
    public abstract calendarService: Type<CalendarService>;
    public abstract veterinarianService?: Type<VeterinarianService>;
    public abstract onlinePaymentService: Type<OnlinePaymentService>;
    public abstract pdfViewerService: Type<PdfViewerService>;
    public abstract selfCheckInService: Type<SelfCheckInService>;
    public abstract mediaViewerService: Type<MediaViewerService>;
    public abstract mayBaseService: Type<MayBaseService>;
    public abstract notificationService: Type<NotificationService>;
    public abstract pushNotificationService: Type<PushNotificationService>;
    public abstract navigationStackService: Type<NavigationStackService>;
    public abstract veterinarianSearchService: Type<VeterinarianSearchService>;
    public abstract appStoreService: Type<AppStoreService>;
    public abstract statisticService: Type<StatisticService>;
}

@NgModule({
    imports: [CommonModule, ApiModule],
})
export class SharedServicesModule {
    // This can be extended to be generic and provide any default implementation.
    public static getMayBaseDefault(): Type<MayBaseService> {
        return DefaultMayBaseService;
    }

    public static forRoot(config: SharedServicesConfig): ModuleWithProviders<SharedServicesModule> {
        return {
            ngModule: SharedServicesModule,
            providers: [
                FeatureService,
                { provide: TitleStrategy, useClass: TranslatedTitleStrategy },
                {
                    provide: AccountService,
                    useClass: config.accountService ?? DefaultAccountService,
                },
                {
                    provide: ActivityStreamService,
                    useClass: config.activityStreamService ?? DefaultActivityStreamService,
                },
                {
                    provide: CommonService,
                    useClass: config.commonService ?? DefaultCommonService,
                },
                {
                    provide: LoginService,
                    useClass: config.loginService ?? DefaultLoginService,
                },
                {
                    provide: TermsOfUseService,
                    useClass: config.termsOfUseService ?? DefaultTermsOfUseService,
                },
                {
                    provide: UserService,
                    useClass: config.userService ?? DefaultUserService,
                },
                {
                    provide: FormatService,
                    useClass: config.formatService ?? DefaultFormatService,
                },
                {
                    provide: PxlApplicationStateService,
                    useClass: config.applicationStateService,
                },
                {
                    provide: LoadingService,
                    useClass: config.loadingService,
                },
                {
                    provide: RouteReuseService,
                    useClass: config.routeReuseService ?? DefaultRouteReuseService,
                },
                {
                    provide: ThemeService,
                    useClass: config.themeService ?? DefaultThemeService,
                },
                {
                    provide: FavoriteReferenceDataService,
                    useClass: config.favoriteReferenceDataService ?? DefaultFavoriteReferenceDataService,
                },
                {
                    provide: StorageService,
                    useClass: config.storageService,
                },
                {
                    provide: PlatformService,
                    useClass: config.platformService,
                },
                {
                    provide: ShareService,
                    useClass: config.shareService,
                },
                {
                    provide: StatusBarService,
                    useClass: config.statusBarService,
                },
                {
                    provide: DeviceService,
                    useClass: config.deviceService,
                },
                {
                    provide: SafeAreaService,
                    useClass: config.safeAreaService,
                },
                {
                    provide: SplashScreenService,
                    useClass: config.splashScreenService,
                },
                {
                    provide: CameraService,
                    useClass: config.cameraService,
                },
                {
                    provide: PermissionService,
                    useClass: config.permissionService,
                },
                {
                    provide: GeolocationService,
                    useClass: config.geolocationService,
                },
                {
                    provide: DeepLinkService,
                    useClass: config.deepLinkService,
                },
                {
                    provide: KeyboardService,
                    useClass: config.keyboardService,
                },
                {
                    provide: BrowserService,
                    useClass: config.BrowserService,
                },
                {
                    provide: ScreenOrientationService,
                    useClass: config.screenOrientationService,
                },
                {
                    provide: CalendarService,
                    useClass: config.calendarService,
                },
                { provide: PatientService, useClass: config.patientService ?? DefaultPatientService },
                { provide: VeterinarianService, useClass: config.veterinarianService ?? DefaultVeterinarianService },
                { provide: OnlinePaymentService, useClass: config.onlinePaymentService ?? DefaultOnlinePaymentService },
                { provide: FinancesService, useClass: config.financesService },
                { provide: HealthRecordService, useClass: config.healthRecordService },
                { provide: AssignmentPlanService, useClass: DefaultAssignmentPlanService },
                { provide: MedicationPlanService, useClass: DefaultMedicationPlanService },
                { provide: AppointmentService, useClass: DefaultAppointmentService },
                { provide: MedicationService, useClass: DefaultMedicationService },
                { provide: PdfViewerService, useClass: config.pdfViewerService },
                { provide: MediaViewerService, useClass: config.mediaViewerService },
                { provide: DocumentViewerService, useClass: DefaultDocumentViewerService },
                { provide: SelfCheckInService, useClass: DefaultSelfCheckInService },
                { provide: KnowledgeDbService, useClass: DefaultKnowledgeDbService },
                { provide: MayBaseService, useClass: config.mayBaseService },
                { provide: ContactFormService, useClass: config.contactFormService ?? DefaultContactFormService },
                { provide: NotificationService, useClass: config.notificationService },
                { provide: PushNotificationService, useClass: config.pushNotificationService },
                { provide: LoggingService, useClass: DefaultLoggingService },
                { provide: VeterinarianLinkageRequestService, useClass: DefaultVeterinarianLinkageRequestService },
                {
                    provide: NavigationStackService,
                    useClass: Capacitor.getPlatform() === 'android' ? config.navigationStackService : null,
                },
                { provide: DocumentsFromVetService, useClass: DefaultDocumentsFromVetService },
                { provide: UnregisteredDataService, useClass: DefaultUnregisteredDataService },
                { provide: DocumentReferenceService, useClass: DefaultDocumentReferenceService },
                { provide: VeterinarianSearchService, useClass: DefaultVeterinarianSearchService },
                { provide: SystemBroadcastService, useClass: DefaultSystemBroadcastService },
                { provide: AnimalFeedingService, useClass: DefaultAnimalFeedingService },
                { provide: AppReviewService, useClass: DefaultAppReviewService },
                { provide: AppStoreService, useClass: config.appStoreService },
                { provide: StatisticService, useClass: config.statisticService ?? DefaultStatisticService },
            ],
        };
    }
}
